import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ToolBar } from "@/components/ToolBar";
import { ExportMenu } from "@/components/ExportMenu";
import { Plus, PencilSimple, Star, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { ArchiveButton } from "@/components/ArchiveButton";

const EMPTY = { name: "", contact_person: "", email: "", phone: "", address: "", tax_id: "", bank_name: "", bank_account: "", rating: 0, on_time_delivery: 0, quality_score: 0, notes: "" };

export default function SuppliersPage() {
  const { user } = useAuth();
  const canDelete = can(user, ["admin"]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  // Supplier Statement download dialog — pick From / To (defaults to the
  // current calendar month) and whether to show all bills or paid-only.
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtSupplier, setStmtSupplier] = useState(null);
  const [stmtStart, setStmtStart] = useState("");
  const [stmtEnd, setStmtEnd] = useState("");
  const [stmtPaidOnly, setStmtPaidOnly] = useState(false);
  const [stmtDownloading, setStmtDownloading] = useState(false);

  function openStatementDialog(s) {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
    setStmtSupplier(s);
    setStmtStart(`${y}-${m}-01`);
    setStmtEnd(`${y}-${m}-${String(lastDay).padStart(2, "0")}`);
    setStmtPaidOnly(false);
    setStmtOpen(true);
  }

  async function downloadStatement({ withRange } = { withRange: true }) {
    if (!stmtSupplier) return;
    setStmtDownloading(true);
    try {
      const qs = new URLSearchParams();
      if (withRange && stmtStart) qs.set("start_date", stmtStart);
      if (withRange && stmtEnd) qs.set("end_date", stmtEnd);
      if (stmtPaidOnly) qs.set("paid_only", "true");
      const url = `/exports/supplier/${stmtSupplier.id}/statement.pdf${qs.toString() ? `?${qs}` : ""}`;
      const suffix = withRange && stmtStart && stmtEnd ? `_${stmtStart}_to_${stmtEnd}` : "";
      await downloadFile(url, `${(stmtSupplier.name || "supplier").replace(/\s+/g, "_")}_statement${suffix}.pdf`);
      toast.success("Statement downloaded");
      setStmtOpen(false);
    } catch { toast.error("Download failed"); }
    finally { setStmtDownloading(false); }
  }

  async function load() {
    const { data } = await api.get("/suppliers", { params: { ...(search ? { search } : {}), ...(showArchived ? { include_archived: true } : {}) } });
    setItems(data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, showArchived]);

  async function submit(e) {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        rating: Number(form.rating) || 0,
        on_time_delivery: Number(form.on_time_delivery) || 0,
        quality_score: Number(form.quality_score) || 0,
      };
      if (editing) { await api.put(`/suppliers/${editing.id}`, payload); toast.success("Supplier updated"); }
      else { await api.post("/suppliers", payload); toast.success("Supplier added"); }
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  }

  async function remove(s) {
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success(`${s.name} archived`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Archive failed"); }
  }

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2">Module 06</div>
          <h1 className="section-title text-3xl sm:text-4xl">Suppliers</h1>
          <p className="text-sm text-muted-foreground mt-1">Supplier profiles, compliance and performance metrics.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="new-supplier-btn"><Plus size={16} className="mr-2" /> New Supplier</Button>
      </div>

      <ToolBar search={search} onSearch={setSearch} placeholder="Search suppliers…" showArchived={showArchived} onToggleArchived={setShowArchived} />

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table className="min-w-[900px]">
          <TableHeader>
            <TableRow>
              <TableHead>Supplier</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Bank</TableHead>
              <TableHead className="text-right">Rating</TableHead>
              <TableHead className="text-right">On-time</TableHead>
              <TableHead className="text-right">Quality</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">No suppliers.</TableCell></TableRow>
            ) : items.map((s) => (
              <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                <TableCell>
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{s.address || "—"}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{s.contact_person || "—"}</div>
                  <div className="text-xs text-muted-foreground">{s.email || s.phone || ""}</div>
                </TableCell>
                <TableCell className="text-sm">
                  <div>{s.bank_name || "—"}</div>
                  <div className="text-xs text-muted-foreground font-mono">{s.bank_account || ""}</div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1 tabular-nums"><Star size={12} weight="fill" className="text-amber-500" />{s.rating?.toFixed(1) || "—"}</div>
                </TableCell>
                <TableCell className="text-right tabular-nums">{s.on_time_delivery || 0}%</TableCell>
                <TableCell className="text-right tabular-nums">{s.quality_score || 0}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" className="border-border" onClick={() => { setEditing(s); setForm({ ...EMPTY, ...s }); setOpen(true); }} data-testid={`edit-supplier-${s.id}`}><PencilSimple size={14} /></Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-border"
                      title="Print Statement of Account"
                      onClick={() => openStatementDialog(s)}
                      data-testid={`statement-supplier-${s.id}`}
                    >
                      <Printer size={14} />
                    </Button>
                    <ExportMenu endpointPath={`/exports/supplier/${s.id}`} baseName={`supplier_${s.name.replace(/\s+/g,'_')}`} testId={`export-supplier-${s.id}`} />
                    {canDelete && (
                      <ArchiveButton endpoint={`/suppliers/${s.id}`} label={s.name} onArchived={load} testId={`archive-supplier-${s.id}`} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading font-black">{editing ? "Edit Supplier" : "New Supplier"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-border" data-testid="supplier-name-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="border-border" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-border" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border-border" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="border-border" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Tax ID</Label><Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} className="border-border" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Bank Name</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="border-border" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Bank Account</Label><Input value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} className="border-border font-mono" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Rating (0–5)</Label><Input type="number" min="0" max="5" step="0.1" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="border-border tabular-nums" data-testid="supplier-rating-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">On-time Delivery %</Label><Input type="number" min="0" max="100" step="1" value={form.on_time_delivery} onChange={(e) => setForm({ ...form, on_time_delivery: e.target.value })} className="border-border tabular-nums" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Quality Score (0–100)</Label><Input type="number" min="0" max="100" step="1" value={form.quality_score} onChange={(e) => setForm({ ...form, quality_score: e.target.value })} className="border-border tabular-nums" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-border" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="supplier-submit-btn">{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Supplier Statement — pick date range and paid-only for a monthly AP */}
      {/* ledger. Skip the range with the "All-time" button when reconciling  */}
      {/* the full history. */}
      <Dialog open={stmtOpen} onOpenChange={setStmtOpen}>
        <DialogContent data-testid="supplier-statement-dialog">
          <DialogHeader><DialogTitle className="font-heading font-black">Statement — {stmtSupplier?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Pick the period to print. Activity before <b>From</b> collapses into an Opening Balance line; activity after <b>To</b> is excluded.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">From</Label>
                <Input type="date" value={stmtStart} onChange={(e) => setStmtStart(e.target.value)} className="border-border" data-testid="stmt-from" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">To</Label>
                <Input type="date" value={stmtEnd} onChange={(e) => setStmtEnd(e.target.value)} className="border-border" data-testid="stmt-to" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={stmtPaidOnly}
                onChange={(e) => setStmtPaidOnly(e.target.checked)}
                data-testid="stmt-paid-only"
              />
              Show paid bills only
            </label>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="ghost" onClick={() => setStmtOpen(false)}>Cancel</Button>
            <Button
              type="button"
              variant="outline"
              className="border-border"
              onClick={() => downloadStatement({ withRange: false })}
              disabled={stmtDownloading}
              data-testid="stmt-download-all-time"
            >
              All-time
            </Button>
            <Button
              type="button"
              onClick={() => downloadStatement({ withRange: true })}
              disabled={stmtDownloading || !stmtStart || !stmtEnd}
              data-testid="stmt-download-range"
            >
              Download {stmtStart && stmtEnd ? `${stmtStart} → ${stmtEnd}` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
